import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import * as cheerio from "cheerio";

import { faChevronDoubleUp, faChevronDoubleDown } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { ERROR_TYPE, translateError } from "@/utils";
import AppLogo from "@/assets/images/favicon.png";
import { classNames } from "@/utils/classNames";
import { Icon } from "@/components/Icon/Icon";

interface FormContainerProps {
	ref?: React.RefObject<HTMLDivElement>;
	children: React.ReactNode;
	formFooter?: React.ReactNode;
	formHeader?: React.ReactNode;
	icon?: React.ReactNode;
	submitButtonText: React.ReactNode;
	isLoading: boolean;
	handleSubmit: () => void;
	title: string;
	description?: string;
	descriptionClass?: string;
	descriptionWrapperClass?: string;
	hideActions?: boolean;
	secondaryButtonText?: React.ReactNode;
	secondaryButtonAction?: () => void;
	formContainerClassName?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	formError?: any;
	showLogo?: boolean;
	actionContainerClassName?: string;
	changeActionsDirection?: boolean;
	submitButtonType?: "submit" | "button" | "reset" | undefined;
	secondaryButtonType?: "submit" | "button" | "reset" | undefined;
	disableSecondaryButton?: boolean;
	disableSubmitButton?: boolean;
	category?: string;
	titleClass?: string;
	additionalInformation?: string;
	measure?: string;
	recommendation?: string;
	toDoDescription?: string;
	toDoName?: string;
	subTaskName?: string;
	requirement?: string;
}

export default function FormContainer({
	ref,
	toDoDescription,
	requirement,
	children,
	subTaskName,
	submitButtonText,
	isLoading,
	formHeader,
	description,
	title,
	handleSubmit,
	formFooter = <></>,
	icon = <img alt="logo" className="w-[34px] h-[28px] text-primary-blue" src={`${AppLogo}`} />,
	hideActions = false,
	secondaryButtonAction,
	secondaryButtonText,
	formContainerClassName = "-mt-32",
	formError,
	showLogo = true,
	actionContainerClassName,
	changeActionsDirection = false,
	secondaryButtonType = "submit",
	submitButtonType = "submit",
	disableSecondaryButton = false,
	disableSubmitButton = false,
	titleClass,
	descriptionClass,
	descriptionWrapperClass,
	additionalInformation,
	measure,
	recommendation,
}: FormContainerProps) {
	const [error, setFormError] = useState<string>();
	const { t } = useTranslation();

	const [showMeasure, setShowMeasure] = useState(false);
	const [showAdditionalInformation, setShowAdditionalInformation] = useState(false);
	const [showRequirement, setShowRequirement] = useState(false);

	useEffect(() => {
		if (formError?.data?.message && typeof formError.data.message === "string") {
			setFormError(
				translateError[formError.data.message.toUpperCase() as unknown as ERROR_TYPE] ??
					t("basics.request-error")
			);
		} else if (formError?.data?.message && typeof formError.data.message === "object") {
			// Handle Zod validation errors that return an object
			setFormError(t("basics.request-error"));
		} else if (typeof formError === "string") {
			setFormError(formError);
		}
	}, [formError, t]);

	return (
		<form
			className={`p-1 flex shadow-lg hover:shadow-xl transition-shadow duration-300 ${
				hideActions ? "" : "pb-4"
			} relative bg-surface font-urbanist rounded-xl w-[600px] max-w-[100%] border border-border flex flex-col gap-4 ${formContainerClassName}`}
			onSubmit={() => {
				handleSubmit();
			}}
		>
			<div
				ref={ref}
				className={classNames(
					"bg-white p-4 md:p-6 shadow-sm rounded-lg",
					descriptionWrapperClass || ""
				)}
			>
				{formHeader}
				{showLogo && (
					<div className="mb-4 w-[44px] max-w-[100%] flex items-center justify-center shadow-medium h-[44px] rounded-xl border-[0.28px] border-border">
						{icon}
					</div>
				)}
				{measure && (
					<div
						className="bg-accent-tint py-1 px-2 rounded-lg group flex items-start justify-between cursor-pointer mb-4"
						onClick={() => setShowMeasure(!showMeasure)}
					>
						{showMeasure ? (
							<div
								className={classNames(
									"text-tag text-[14px] leading-[20px] max-w-[calc(100%-20px)] cursor-pointer",
									showMeasure
										? "overflow-visible whitespace-normal"
										: "overflow-hidden whitespace-normal break-words"
								)}
							>
								<div
									dangerouslySetInnerHTML={{
										__html: `<b>${t("basics.measure")}:</b>  ${cheerio
											.load(measure ?? "")
											.text()} ${toDoDescription}`,
									}}
									className={classNames(
										"text-tag text-[14px] leading-[20px] max-w-[calc(100%-20px)] cursor-pointer overflow-hidden  ",
										showMeasure ? "overflow-visible" : "whitespace-nowrap text-ellipsis"
									)}
								/>
							</div>
						) : (
							<div
								className={classNames(
									"text-tag text-[14px] leading-[20px] max-w-[calc(100%-20px)] cursor-pointer",
									showMeasure
										? "overflow-visible whitespace-normal"
										: "overflow-hidden whitespace-normal break-words"
								)}
							>
								<b>{t("basics.measure")}:</b> {cheerio.load(measure ?? "").text()}
							</div>
						)}
						<Icon
							className="w-3 h-3 text-primary-gray- mt-1"
							icon={showMeasure ? faChevronDoubleUp : faChevronDoubleDown}
						/>
					</div>
				)}

				{subTaskName && (
					<div className="bg-gray-200/50 py-1 px-2 rounded-lg group flex items-start justify-between cursor-default mb-4">
						<div
							className={classNames(
								"text-tag text-[14px] leading-[20px] max-w-[calc(100%-20px)] ",

								"overflow-hidden whitespace-normal break-words"
							)}
						>
							<b>{t("basics.subTaskTitle")}:</b> {cheerio.load(subTaskName ?? "").text()}
						</div>
					</div>
				)}

				{requirement && (
					<div
						className="bg-gray-200/50 py-1 px-2 rounded-lg group flex items-start justify-between cursor-pointer mb-4"
						onClick={() => setShowRequirement(!showRequirement)}
					>
						{showRequirement ? (
							<div
								dangerouslySetInnerHTML={{
									__html: `<b>${t("to-do.requirement")}:</b> ${requirement}`,
								}}
								className={classNames(
									"text-tag text-[14px] leading-[20px] max-w-[calc(100%-20px)] cursor-pointer overflow-hidden  ",
									showRequirement ? "overflow-visible" : "whitespace-nowrap text-ellipsis"
								)}
							/>
						) : (
							<div
								className={classNames(
									"text-tag text-[14px] leading-[20px] max-w-[calc(100%-20px)] cursor-pointer overflow-hidden  ",
									showRequirement ? "overflow-visible" : "whitespace-nowrap text-ellipsis"
								)}
							>
								<b>{t("to-do.requirement")}:</b> {cheerio.load(requirement ?? "").text()}
							</div>
						)}

						<Icon
							className="w-3 h-3 text-primary-gray- mt-1"
							icon={showRequirement ? faChevronDoubleUp : faChevronDoubleDown}
						/>
					</div>
				)}

				{additionalInformation && (
					<div
						className="bg-green-200/50 py-1 px-2 rounded-lg group flex items-start justify-between cursor-pointer mb-4"
						onClick={() => setShowAdditionalInformation(!showAdditionalInformation)}
					>
						{showAdditionalInformation ? (
							<div
								dangerouslySetInnerHTML={{
									__html: `<b>${
										recommendation
											? `${t("to-do.recommendation")}`
											: `${t("basics.additionalInfo")}`
									}:</b> ${additionalInformation}`,
								}}
								className={classNames(
									"text-tag text-[14px] leading-[20px] max-w-[calc(100%-20px)] cursor-pointer overflow-hidden  ",
									showAdditionalInformation ? "overflow-visible" : "whitespace-nowrap text-ellipsis"
								)}
							/>
						) : (
							<div
								className={classNames(
									"text-tag text-[14px] leading-[20px] max-w-[calc(100%-20px)] cursor-pointer overflow-hidden  ",
									showAdditionalInformation ? "overflow-visible" : "whitespace-nowrap text-ellipsis"
								)}
							>
								<b>
									{recommendation
										? `${t("to-do.recommendation")}`
										: `${t("basics.additionalInfo")}`}
									:
								</b>{" "}
								{cheerio.load(additionalInformation).text()}
							</div>
						)}

						<Icon
							className="w-3 h-3 text-primary-gray- mt-1"
							icon={showAdditionalInformation ? faChevronDoubleUp : faChevronDoubleDown}
						/>
					</div>
				)}

				<h2
					className={classNames(
						"mb-[6px] text-primary-gray text-smd/6 md:text-[28px] md:leading-[34px] font-bold",
						titleClass || ""
					)}
				>
					{title}
				</h2>

				{description && !description.includes("<") && (
					<p
						className={classNames(
							"text-primary-gray-lighter text-[14px] leading-[20px] mb-6",
							descriptionClass || ""
						)}
					>
						{description}
					</p>
				)}
				{description && description.includes("<") && (
					<div
						dangerouslySetInnerHTML={{ __html: description }}
						className={classNames(
							"text-primary-gray-lighter text-[14px] leading-[20px] mb-6",
							descriptionClass || ""
						)}
					/>
				)}
				{children}
				{formFooter}
			</div>
			{!hideActions || (submitButtonText && secondaryButtonText) ? (
				changeActionsDirection ? (
					<div className={`px-3 space-y-2 flex ${actionContainerClassName}`}>
						{secondaryButtonText && secondaryButtonAction && (
							<Button
								className="!h-[44px]"
								color={ButtonColor.ACTION_SECONDARY}
								disabled={disableSecondaryButton}
								isLoading={isLoading}
								size={ButtonSize.ML}
								title={secondaryButtonText}
								type={secondaryButtonType ?? "submit"}
								onClick={secondaryButtonAction}
							/>
						)}
						{submitButtonText && (
							<Button
								className="!h-[44px]"
								disabled={disableSubmitButton}
								isLoading={isLoading}
								size={ButtonSize.ML}
								title={submitButtonText}
								type={submitButtonType ?? "submit"}
								onClick={() => {
									handleSubmit();
								}}
							/>
						)}
					</div>
				) : (
					<div className={`px-3 space-y-2 ${actionContainerClassName}`}>
						{submitButtonText && (
							<Button
								className="!h-[44px]"
								disabled={disableSubmitButton}
								isLoading={isLoading}
								size={ButtonSize.ML}
								title={submitButtonText}
								type={submitButtonType ?? "submit"}
								onClick={() => {
									handleSubmit();
								}}
							/>
						)}
						{secondaryButtonText && secondaryButtonAction && (
							<Button
								className="!h-[44px]"
								color={ButtonColor.ACTION_SECONDARY}
								disabled={disableSecondaryButton}
								isLoading={isLoading}
								size={ButtonSize.ML}
								title={secondaryButtonText}
								type={secondaryButtonType ?? "submit"}
								onClick={secondaryButtonAction}
							/>
						)}
					</div>
				)
			) : (
				<></>
			)}
			{error && (
				<p className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-[14px] text-danger">
					{error}
				</p>
			)}
		</form>
	);
}
